import { ReactElement, ReactNode } from 'react'
import { Box, SxProps, Theme, useTheme } from '@mui/material'
import Results2RelationLinksRow, {
  type Results2RelationLink,
} from '../Results2DetailPane/components/Results2RelationLinksRow'

interface Props {
  title: ReactNode
  subtitle: ReactNode
  /** Baugruppe / Aufbau line shown between subtitle and relation links. */
  assemblyLine?: ReactNode
  relationLinks?: Results2RelationLink[]
  sx?: SxProps<Theme>
}

/** Identity column metrics — kept in sync with {@link ElementStatusHeader}. */
const identityColumnSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.35,
} as const

const titleSx = (theme: Theme) =>
  ({
    fontSize: 20,
    fontWeight: 500,
    color: theme.palette.text.primary,
    fontFamily: 'Roboto',
    lineHeight: 1.1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as const)

/**
 * Identity strip aligned with {@link ElementStatusHeader}: same padding, inner Boxes, and
 * title/subtitle typography (identity column only — no η row).
 */
const detailSubtitleSx = (theme: Theme) =>
  ({
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontFamily: 'Roboto',
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  } as const)

const Results2PaneHeader = ({
  title,
  subtitle,
  assemblyLine,
  relationLinks = [],
  sx,
}: Props): ReactElement => {
  const theme = useTheme()

  return (
    <Box
      sx={[
        {
          flexShrink: 0,
          background: '#FAFAFB',
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: 1.75,
          pt: 1.25,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box sx={identityColumnSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            maxWidth: '100%',
            lineHeight: 1,
          }}
        >
          <Box component="span" sx={titleSx(theme)}>
            {title}
          </Box>
        </Box>
        <Box component="span" sx={detailSubtitleSx(theme)}>
          {subtitle}
        </Box>
        {assemblyLine ? (
          <Box component="span" sx={detailSubtitleSx(theme)}>
            {assemblyLine}
          </Box>
        ) : null}
        <Results2RelationLinksRow links={relationLinks} />
      </Box>
    </Box>
  )
}

export default Results2PaneHeader
